import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';

import { cn } from 'lib/utils';

type SliderThumbProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Thumb
>;

type SliderProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
> & {
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
  thumbs?: SliderThumbProps[];
};

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      trackClassName,
      rangeClassName,
      thumbClassName,
      thumbs,
      ...props
    },
    ref
  ) => {
    const { value, defaultValue } = props;
    const thumbCount =
      thumbs?.length ??
      (Array.isArray(value) ? value.length : undefined) ??
      (Array.isArray(defaultValue) ? defaultValue.length : undefined) ??
      1;
    const thumbsToRender =
      thumbs ??
      Array.from({ length: thumbCount }, () => ({} as SliderThumbProps));

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          className
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            'relative h-2 w-full grow overflow-hidden rounded-full bg-secondary',
            trackClassName
          )}
        >
          <SliderPrimitive.Range
            className={cn('absolute h-full bg-primary', rangeClassName)}
          />
        </SliderPrimitive.Track>
        {thumbsToRender.map((thumbProps, index) => {
          const { className: thumbItemClassName, ...restThumbProps } =
            thumbProps ?? {};
          return (
            <SliderPrimitive.Thumb
              key={index}
              className={cn(
                'block h-5 w-5 cursor-grab rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:cursor-grabbing disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
                thumbClassName,
                thumbItemClassName
              )}
              {...restThumbProps}
            />
          );
        })}
      </SliderPrimitive.Root>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
